import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  Alert,
  Button,
  Card,
  OptionCard,
  RadioGroup,
  Skeleton,
  SkeletonGroup,
} from '@/components/ui';
import { LocationPicker, type PickedLocation } from '@/features/location/LocationPicker';
import { DEFAULT_CENTER, type LatLng } from '@/features/location/mapProvider';
import { PlaceSearch } from '@/features/location/PlaceSearch';
import type { AddressOut, ReverseGeocodeOut } from '@/lib/api/types';
import { deliveryDetailsSchema, type DeliveryDetailsValues } from '@/lib/validators';

import { AddressDetailsFields } from './AddressDetailsFields';
import {
  HOME_STATE,
  NEW_ADDRESS,
  fromSaved,
  labelTaken,
  labelTakenMessage,
  savedLines,
  savedSummary,
  type AddressPhase,
  type DeliverySubmit,
} from './model';
import { PrintingHoursNotice } from './PrintingHoursNotice';

export interface DeliveryAddressProps {
  /** Wired to the sticky submit button via `form="..."`, outside the form element. */
  formId: string;
  saved: AddressOut[];
  savedLoading: boolean;
  /** Which saved address is selected, or NEW_ADDRESS. Owned by the step. */
  selectedAddressId: string;
  onSelectAddress: (id: string) => void;
  /** The pin. Owned by the step, because the price is quoted against it. */
  location: PickedLocation | null;
  onLocationChange: (location: PickedLocation) => void;
  /** What the server calls the pin. The step asks; this renders the answer. */
  geocode: { data: ReverseGeocodeOut | undefined; isFetching: boolean; isError: boolean };
  /** Which of the two screens is showing. Owned by the step: the action bar changes with it. */
  phase: AddressPhase;
  onPhaseChange: (phase: AddressPhase) => void;
  /** The device fix, so the step can say how far the pin has drifted from it. */
  onDeviceFix: (coordinates: LatLng) => void;
  /**
   * A PIN code the customer typed because the map had none. Memoised by the
   * caller: it is reported from an effect.
   */
  onPincodeOverride: (pincode: string | null) => void;
  onValidSubmit: (values: DeliverySubmit) => void;
  /** Field messages from a server 422, keyed by field name. */
  serverFieldErrors?: Record<string, string>;
  disabled?: boolean;
  /** Prefilled from the signed-in profile: the receiver is usually yourself. */
  defaultPhone?: string;
  defaultName?: string;
}

/**
 * The delivery address, built the way every delivery app builds it.
 *
 * TWO SCREENS, NOT ONE FORM. The old form asked for seven things — name,
 * phone, street, area, city, state, PIN — under a map, and six of them were
 * required. Zepto, Swiggy and Zomato ask for ONE, because the pin answers the
 * rest: the map names the area, and the area carries the city, the state and
 * the PIN code. So:
 *
 *   1. THE POINT. The map is the screen. Search floats over the top of it, the
 *      locate button over the bottom, and the pin is fixed at the centre. As
 *      the customer settles the map the server names what is under the pin
 *      and the action bar shows it — "Chembumukku, Kochi 682021" — with one
 *      button: Confirm location.
 *
 *   2. THE DETAILS. The named area as a read-only line with "Change", then
 *      the one line the map cannot know — flat or house number and floor —
 *      a landmark if they want, a name to save it under, and the receiver
 *      collapsed to a row because it is usually themselves.
 *
 * City, state and PIN code are NOT TYPED. They come from the pin, and they
 * reach the server as what the pin said. The one exception is a pin the map
 * cannot name — a new layout, a village lane — where the form asks for the
 * PIN code and the area, because those two the customer does know.
 *
 * A SAVED ADDRESS SKIPS BOTH. Picking one adopts its pin and its lines; the
 * step prices it and the customer presses Continue. Nothing is re-asked.
 */
export function DeliveryAddress({
  formId,
  saved,
  savedLoading,
  selectedAddressId,
  onSelectAddress,
  location,
  onLocationChange,
  geocode,
  phase,
  onPhaseChange,
  onDeviceFix,
  onPincodeOverride,
  onValidSubmit,
  serverFieldErrors = {},
  disabled = false,
  defaultPhone = '',
  defaultName = '',
}: DeliveryAddressProps) {
  const form = useForm<DeliveryDetailsValues>({
    resolver: zodResolver(deliveryDetailsSchema),
    mode: 'onBlur',
    defaultValues: {
      line1: '',
      landmark: '',
      label: '',
      name: defaultName,
      phone: defaultPhone.replace(/^\+91/, ''),
      city: '',
      pincode: '',
    },
  });
  const { watch, setError, handleSubmit } = form;

  const named = geocode.data?.found ? geocode.data : null;
  // What the map could not tell us, and therefore the customer must. Two
  // separate questions, because a geocoder frequently knows the area and not
  // the postcode.
  const needsPincode = location !== null && !geocode.isFetching && !named?.pincode;
  const needsArea = location !== null && !geocode.isFetching && !named?.city;

  // Somewhere to move the map to, chosen OUTSIDE the map: a search result.
  // The map cannot follow `value` — `value` is mostly set BY the map — so an
  // outside choice says so through its own prop.
  const [recentreOn, setRecentreOn] = useState<LatLng | null>(null);

  function onPlacePicked(coordinates: LatLng): void {
    setRecentreOn(coordinates);
    onLocationChange({ ...coordinates, source: 'map', accuracyM: null });
  }

  const chosenSaved = saved.find((entry) => entry.id === selectedAddressId);
  // Every saved address's name is spoken for. (A new address has no "self"
  // to exclude.)
  const takenLabels = saved.flatMap((entry) => (entry.label ? [entry.label] : []));

  function submit(values: DeliveryDetailsValues): void {
    // The conditional requirements the schema cannot express: asked only
    // when the map came up short, and then they are not optional.
    const pincode = named?.pincode ?? values.pincode?.trim() ?? '';
    const city = named?.city ?? values.city?.trim() ?? '';
    let blocked = false;
    const label = values.label?.trim() ?? '';
    if (labelTaken(label, takenLabels)) {
      setError('label', { type: 'taken', message: labelTakenMessage(label) });
      blocked = true;
    }
    if (!pincode) {
      setError('pincode', { type: 'required', message: 'Enter the 6-digit PIN code.' });
      blocked = true;
    }
    if (!city) {
      setError('city', { type: 'required', message: 'Enter the area or town.' });
      blocked = true;
    }
    if (blocked) return;

    onValidSubmit({
      name: values.name,
      phone: values.phone,
      line1: values.line1,
      landmark: values.landmark?.trim() || null,
      label: values.label?.trim() || null,
      locality: named?.locality ?? null,
      city,
      state: named?.state ?? HOME_STATE,
      pincode,
    });
  }

  // The PIN code the customer typed, when the map could not supply one,
  // reported upward so the delivery is priced against it BEFORE Continue —
  // the same PIN code then goes on the saved address, which is the only
  // pairing checkout accepts.
  const typedPincode = watch('pincode') ?? '';
  useEffect(() => {
    onPincodeOverride(/^[1-9]\d{5}$/.test(typedPincode) ? typedPincode : null);
  }, [onPincodeOverride, typedPincode]);

  return (
    <form
      id={formId}
      noValidate
      onSubmit={(event) => {
        // A saved address has nothing to validate: the new-address fields
        // are not on screen, yet they still hold their empty defaults, and
        // running the schema over them failed the door-number rule with no
        // field to show it on. Continue appeared to do nothing.
        if (chosenSaved) {
          event.preventDefault();
          onValidSubmit(fromSaved(chosenSaved));
          return;
        }
        void handleSubmit(submit)(event);
      }}
      className="flex flex-col gap-4"
    >
      {savedLoading ? (
        <SkeletonGroup label="Loading your saved addresses">
          <Skeleton className="h-20 w-full" />
        </SkeletonGroup>
      ) : saved.length > 0 ? (
        <RadioGroup legend="Deliver to">
          {saved.map((entry) => (
            <OptionCard
              key={entry.id}
              name="saved-address"
              value={entry.id}
              checked={selectedAddressId === entry.id}
              onChange={() => onSelectAddress(entry.id)}
              label={entry.label || entry.name}
              description={savedSummary(entry)}
            />
          ))}
          <OptionCard
            name="saved-address"
            value={NEW_ADDRESS}
            checked={selectedAddressId === NEW_ADDRESS}
            onChange={() => {
              onSelectAddress(NEW_ADDRESS);
              onPhaseChange('point');
            }}
            label="Add a new address"
          />
        </RadioGroup>
      ) : null}

      {chosenSaved ? (
        <SavedAddressCard address={chosenSaved} />
      ) : phase === 'point' ? (
        <div className="-mx-4 sm:mx-0">
          <LocationPicker
            appearance="screen"
            value={location}
            onChange={onLocationChange}
            centreOn={recentreOn}
            onDeviceFix={onDeviceFix}
            overlay={<PlaceSearch bias={DEFAULT_CENTER} onPicked={onPlacePicked} floating />}
          />
        </div>
      ) : (
        <>
          {/* The map's answer, read back. One line and a way out of it. */}
          <Card title="Delivering to">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-ink">
                  {named?.locality ?? named?.city ?? 'Your pin'}
                </p>
                {named?.formatted ? (
                  <p className="text-sm text-ink-muted">{named.formatted}</p>
                ) : null}
              </div>
              <Button type="button" variant="secondary" onClick={() => onPhaseChange('point')}>
                Change
              </Button>
            </div>
          </Card>

          <AddressDetailsFields
            form={form}
            needsArea={needsArea}
            needsPincode={needsPincode}
            serverFieldErrors={serverFieldErrors}
            disabled={disabled}
            takenLabels={takenLabels}
          />
        </>
      )}

      {/* Under the delivery answer, because that is what it qualifies.
          Renders nothing during printing hours. */}
      <PrintingHoursNotice />
    </form>
  );
}

/**
 * A saved address, read back. Nothing here is editable: the customer answered
 * every question when they saved it, and corrections belong on the account
 * page, not in the middle of paying.
 */
function SavedAddressCard({ address }: { address: AddressOut }) {
  return (
    <Card title={address.label || 'Delivering to'}>
      <address className="text-sm not-italic text-ink-muted">
        {savedLines(address)}
        <br />
        {address.name} &middot; {address.phone}
      </address>
      {address.latitude == null ? (
        <Alert severity="warning" title="This address has no pin" className="mt-3">
          <p>Add it again as a new address so the rider has one.</p>
        </Alert>
      ) : null}
    </Card>
  );
}
